if (!customElements.get('cart-recommended-products')) {
  customElements.define(
    'cart-recommended-products',
    class CartRecommendedProducts extends HTMLElement {
      constructor() {
        super();

        this.cartUpdateUnsubscriber = undefined;
        this.abortController = undefined;
      }

      connectedCallback() {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => this.onCartUpdate(event));
        this.initAccordionAccessibility();

        if (this.dataset.productId) {
          this.refresh(this.dataset.productId);
        }
      }

      disconnectedCallback() {
        this.abortPendingRequest();

        if (this.cartUpdateUnsubscriber) {
          this.cartUpdateUnsubscriber();
        }
      }

      onCartUpdate(event = {}) {
        const seedProductId = this.getSeedProductId(event.cartData, event.productVariantId || event.variantId);

        if (seedProductId) {
          this.dataset.productId = seedProductId;
          return this.refresh(seedProductId);
        }

        delete this.dataset.productId;
        return this.renderFallback();
      }

      getSeedProductId(cartData, variantId) {
        const items = cartData?.items;
        if (!items?.length) return '';

        if (variantId) {
          const matchedItem = items.find((item) => `${item.variant_id}` === `${variantId}`);
          if (matchedItem?.product_id) return `${matchedItem.product_id}`;
        }

        const firstAvailableItem = items.find((item) => item.product_id);
        return firstAvailableItem?.product_id ? `${firstAvailableItem.product_id}` : '';
      }

      async refresh(productId) {
        this.abortPendingRequest();
        this.abortController = new AbortController();
        const { signal } = this.abortController;

        this.classList.add('is-loading');

        try {
          const [recommendationsData, recommendationsMarkup] = await Promise.all([
            this.fetchRecommendationsData(productId, signal),
            this.fetchRecommendationsMarkup(productId, signal),
          ]);

          this.replaceContent(recommendationsMarkup);

          const handles = this.getRecommendationHandles(recommendationsData);
          if (!handles.length) {
            this.finishRender();
            return;
          }

          const cards = await this.fetchRecommendationCards(handles, signal);
          this.renderCards(cards);
          this.finishRender();
        } catch (error) {
          if (error.name === 'AbortError') return;

          console.error(error);
          await this.renderFallback();
        }
      }

      async renderFallback() {
        this.abortPendingRequest();
        this.abortController = new AbortController();

        this.classList.add('is-loading');

        try {
          const fallbackMarkup = await this.fetchFallbackMarkup(this.abortController.signal);
          this.replaceContent(fallbackMarkup);
          this.finishRender();
        } catch (error) {
          if (error.name !== 'AbortError') {
            console.error(error);
          }
        }
      }

      abortPendingRequest() {
        if (this.abortController) {
          this.abortController.abort();
        }
      }

      getRecommendationHandles(recommendationsData) {
        return [...new Set((recommendationsData?.products || [])
          .map((product) => product.handle)
          .filter(Boolean)
        )].slice(0, this.getLimit());
      }

      async fetchRecommendationsData(productId, signal) {
        const url = new URL(this.getRecommendationsJsonUrl(), window.location.origin);
        url.searchParams.set('product_id', productId);
        url.searchParams.set('limit', this.getLimit());
        url.searchParams.set('intent', this.getIntent());

        const response = await fetch(url.toString(), { signal });
        if (!response.ok) throw new Error(`Failed to load recommendations JSON: ${response.status}`);

        return response.json();
      }

      async fetchRecommendationsMarkup(productId, signal) {
        const url = new URL(this.getRecommendationsSectionUrl(), window.location.origin);
        url.searchParams.set('product_id', productId);
        url.searchParams.set('limit', this.getLimit());
        url.searchParams.set('intent', this.getIntent());

        const response = await fetch(url.toString(), { signal });
        if (!response.ok) throw new Error(`Failed to load recommendations markup: ${response.status}`);

        const responseText = await response.text();
        const markup = this.extractSectionContent(responseText);
        if (!markup) throw new Error('Missing recommendations section markup.');

        return markup;
      }

      async fetchFallbackMarkup(signal) {
        const url = new URL(this.getFallbackSectionUrl(), window.location.origin);
        const response = await fetch(url.toString(), { signal });
        if (!response.ok) throw new Error(`Failed to load recommendations fallback: ${response.status}`);

        const responseText = await response.text();
        const markup = this.extractSectionContent(responseText);
        if (!markup) throw new Error('Missing recommendations fallback markup.');

        return markup;
      }

      async fetchRecommendationCards(handles, signal) {
        const requests = handles.map((handle) => this.fetchRecommendationCard(handle, signal));
        const cards = await Promise.all(requests);
        return cards.filter(Boolean);
      }

      async fetchRecommendationCard(handle, signal) {
        const url = new URL(this.getProductSectionBaseUrl(handle), window.location.origin);
        url.searchParams.set('section_id', this.dataset.cardSectionId || 'cart-recommended-product-card');

        const response = await fetch(url.toString(), { signal });
        if (!response.ok) throw new Error(`Failed to load recommendation card for ${handle}: ${response.status}`);

        const responseText = await response.text();
        return this.extractCardMarkup(responseText);
      }

      extractSectionContent(html) {
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        return documentFragment.querySelector('.cart-recommended-products__content');
      }

      extractCardMarkup(html) {
        const documentFragment = new DOMParser().parseFromString(html, 'text/html');
        return documentFragment.querySelector('.cart-recommended-products__slide');
      }

      replaceContent(content) {
        const accordion = this.querySelector('.cart-recommended-products__accordion');
        this.accordionWasExpanded = accordion?.classList.contains('is-expanded') ?? false;
        this.innerHTML = content?.outerHTML || '';
      }

      renderCards(cards) {
        const list = this.querySelector('.cart-recommended-products__list');

        if (!list) return;

        list.innerHTML = cards.map((card) => card.outerHTML).join('');
      }

      finishRender() {
        this.classList.remove('is-loading');
        this.querySelector('.cart-recommended-products__content')?.classList.remove('is-loading');

        const accordion = this.querySelector('.cart-recommended-products__accordion');
        if (accordion && this.accordionWasExpanded) {
          accordion.classList.add('is-expanded');
        }

        delete this.accordionWasExpanded;
        this.initAccordionAccessibility();
      }

      initAccordionAccessibility() {
        const accordion = this.querySelector('.cart-recommended-products__accordion');
        const summary = accordion?.querySelector('summary');
        if (!summary || summary.dataset.accordionInit) return;

        summary.dataset.accordionInit = 'true';
        summary.setAttribute('role', 'button');
        summary.setAttribute('aria-expanded', accordion.classList.contains('is-expanded'));

        const panel = summary.nextElementSibling;
        if (panel?.getAttribute('id')) {
          summary.setAttribute('aria-controls', panel.id);
        }

        summary.addEventListener('click', (event) => {
          event.preventDefault();
          accordion.classList.toggle('is-expanded');
          event.currentTarget.setAttribute('aria-expanded', accordion.classList.contains('is-expanded'));
        });
      }

      getLimit() {
        return parseInt(this.dataset.limit || '4', 10);
      }

      getIntent() {
        return this.dataset.intent || 'related';
      }

      getRecommendationsJsonUrl() {
        return `${this.getShopifyRoot()}recommendations/products.json`;
      }

      getRecommendationsSectionUrl() {
        return `${this.getShopifyRoot()}recommendations/products?section_id=${
          this.dataset.sectionId || 'cart-recommended-products'
        }`;
      }

      getFallbackSectionUrl() {
        return `${this.getShopifyRoot()}?section_id=${this.dataset.sectionId || 'cart-recommended-products'}`;
      }

      getProductSectionBaseUrl(handle) {
        return `${this.getShopifyRoot()}products/${handle}`;
      }

      getShopifyRoot() {
        const root = window.Shopify?.routes?.root || '/';
        return root.endsWith('/') ? root : `${root}/`;
      }
    }
  );
}
